import * as React from "react";
import { observer, inject } from "mobx-react";
import * as moment from "moment";
import { SelectGroup, DateGroup } from "../common/SimpleFormComponents";

import { ConfigurationStore } from "../../stores/ConfigurationStore";
import { ReportStore } from "../../stores/ReportStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faChartBar, faPrint } from "@fortawesome/free-solid-svg-icons";
import { CopyReportIframe, PrintReportIframe } from "../../common/Methods/FileUtil";
import CustomerSelect from "../common/CustomerSelect";
import { SimpleCustomer } from "src/model/Customer";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";
import { HorseForSaleEndpoints } from "../../endpoints/HorseForSaleEndpoints";
import { Indexes, IndexesFriendlyName } from "src/model/Reports";
import { SimpleHorseForSale } from "src/model/HorseForSale";
import HorseForSaleSelect from "../common/HorseForSaleSelect";

export interface StandardProps {
    customerEndpoints?: CustomerEndpoints;
    configurationStore?: ConfigurationStore;
    horseForSaleEndpoints?: HorseForSaleEndpoints;
    reportStore?: ReportStore;

    title: string;
    content: string;
    selectedSaleId?: string | null;

    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    onChangeSelectSale?: (saleId: string | null) => void;

    wasValidated: boolean;
    involvementSaleFilters?: number[] | null;
    involvementRoleFilters?: string[];
}

export interface PropsWithConsignor {
    onChangeConsignor?: (consignor: SimpleCustomer | null) => void;
    consignor?: SimpleCustomer | null;
    customerPlaceholder?: string;
}

export interface PropsWithRoles {
    onChangeSelectRole?: (role: string | null) => void;
    role?: string | null;
}

export interface PropsWithYears {
    onChangeSelectYear?: (year: string | null) => void;
    year?: string | null;
}

export interface PropsWithIndexes {
    onChangeSelectIndex?: (indexId: string | null) => void;
    indexId?: string | null;
}

export interface PropsWithInsuranceCompany {
    onChangeSelectInsuranceCompany?: (companyId: string | null) => void;
    insuranceId?: string | null;
}

export interface PropsWithHorses {
    onChangeSelectHorsesForSale?: (horseForSale: SimpleHorseForSale | null) => void;
    horseForSale?: SimpleHorseForSale | null;
}

export interface PropsWithDates {
    onChangeSelectDateFrom?: (date: number | null) => void;
    onChangeSelectDateTo?: (date: number | null) => void;
    dateFrom?: number | null;
    dateTo?: number | null;
}

type Props = StandardProps &
    PropsWithConsignor &
    PropsWithIndexes &
    PropsWithHorses &
    PropsWithRoles &
    PropsWithDates &
    PropsWithInsuranceCompany &
    PropsWithYears;

export interface State {}

@inject("horseForSaleEndpoints")
@inject("customerEndpoints")
@inject("configurationStore")
@inject("reportStore")
@observer
export default class SimpleReportComponent extends React.Component<Props, State> {
    private reportFrame: HTMLIFrameElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    updateReportContent = (content: string) => {
        if (!this.reportFrame) {
            return;
        }
        const frameDoc = this.reportFrame.contentDocument;
        frameDoc!.open("text/html", "replace");
        frameDoc!.write(content);
        frameDoc!.close();
    };

    copyReport = () => {
        if (!this.reportFrame) {
            return;
        }
        const iframe = this.reportFrame;
        const selection = iframe.contentWindow!.getSelection();
        const range = iframe.contentDocument!.createRange();
        range.selectNodeContents(iframe.contentDocument!.body);
        selection!.removeAllRanges();
        selection!.addRange(range);
        iframe.contentDocument!.execCommand("copy");
    };

    printReport = () => {
        if (!this.reportFrame) {
            return;
        }
        this.reportFrame.focus();
        this.reportFrame.contentWindow!.print();
    };

    componentDidUpdate() {
        this.updateReportContent(this.props.content);
    }

    handleSalesYears = (): Array<string> => {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            throw Error("no configuration Store");
        }
        let years: Array<string> = [];
        configurationStore.horseSales.forEach(hs => {
            hs.dates.forEach(d => {
                const date = moment(d.date)
                    .year()
                    .toString();
                if (!years.includes(date)) {
                    years.push(date);
                }
            });
        });
        return years;
    };

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            return <div />;
        }

        return (
            <>
                <div className="card shadow-sm">
                    <div className="card-header py-2">{this.props.title}</div>
                    <div className="card-body">
                        <form
                            className={"needs-validation " + (this.props.wasValidated ? " was-validated" : "")}
                            onSubmit={e => this.props.onSubmit(e)}
                            noValidate={true}
                        >
                            <div className="form-row">
                                {!this.props.onChangeSelectYear && !this.props.onChangeSelectDateFrom && (
                                    <SelectGroup
                                        label="Select a Sale:"
                                        name="selectedSaleId"
                                        isRequired={true}
                                        className={`col-md-12 ${
                                            this.props.onChangeConsignor ||
                                            this.props.onChangeSelectIndex ||
                                            this.props.onChangeSelectHorsesForSale ||
                                            this.props.onChangeSelectRole ||
                                            this.props.onChangeSelectInsuranceCompany ||
                                            this.props.onChangeSelectYear
                                                ? `col-lg-4`
                                                : `col-lg-8`
                                        }`}
                                        disabled={false}
                                        isInvalid={
                                            this.props.wasValidated && (this.props.selectedSaleId === null || this.props.selectedSaleId === "")
                                        }
                                        value={this.props.selectedSaleId || ""}
                                        onChange={e => this.props.onChangeSelectSale!(e.target.value)}
                                    >
                                        <option value=""> Select a sale...</option>
                                        {configurationStore.horseSales.map(sale => (
                                            <option key={sale.id} value={sale.id}>
                                                {sale.name}
                                            </option>
                                        ))}
                                    </SelectGroup>
                                )}

                                {this.props.onChangeSelectDateFrom && this.props.onChangeSelectDateTo && (
                                    <div className="col-8">
                                        <div className="form-row">
                                            <DateGroup
                                                label="From Date:"
                                                name="dueDate"
                                                className="col-6"
                                                value={this.props.dateFrom ? moment(this.props.dateFrom).format("YYYY/MM/DD") : ""}
                                                disabled={this.props.selectedSaleId === null || this.props.selectedSaleId === ""}
                                                onChange={date => this.props.onChangeSelectDateFrom!(date)}
                                                isRequired={true}
                                                icon={true}
                                            />
                                            <DateGroup
                                                label="To Date:"
                                                name="dueDate"
                                                className="col-6"
                                                value={this.props.dateTo ? moment(this.props.dateTo).format("YYYY/MM/DD") : ""}
                                                disabled={this.props.selectedSaleId === null || this.props.selectedSaleId === ""}
                                                onChange={date => this.props.onChangeSelectDateTo!(date)}
                                                isRequired={true}
                                                icon={true}
                                            />
                                        </div>
                                    </div>
                                )}

                                {this.props.onChangeSelectRole && (
                                    <SelectGroup
                                        label="Role:"
                                        name="role"
                                        isRequired={true}
                                        className="col-4"
                                        disabled={this.props.selectedSaleId === null}
                                        isInvalid={this.props.wasValidated && (this.props.role === null || this.props.role === "")}
                                        value={this.props.role || ""}
                                        onChange={e => this.props.onChangeSelectRole!(e.target.value)}
                                    >
                                        <option value="">Select a Role...</option>
                                        <option value="all">Include All Roles</option>
                                        {configurationStore.roles.map(role => (
                                            <option key={role.searchName} value={role.searchName}>
                                                {role.friendlyName}
                                            </option>
                                        ))}
                                    </SelectGroup>
                                )}

                                {this.props.onChangeSelectYear && (
                                    <SelectGroup
                                        label="Year:"
                                        name="year"
                                        isRequired={true}
                                        className="col-8"
                                        disabled={this.props.selectedSaleId === null}
                                        isInvalid={this.props.wasValidated && (this.props.year === null || this.props.year === "")}
                                        value={this.props.year || ""}
                                        onChange={e => this.props.onChangeSelectYear!(e.target.value)}
                                    >
                                        <option value="">Select a Year...</option>
                                        {this.handleSalesYears().map((year: string) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </SelectGroup>
                                )}

                                {this.props.onChangeConsignor && (
                                    <div className="col-4">
                                        <label htmlFor="consignor">Consignor:</label>
                                        <CustomerSelect
                                            customerEndpoints={this.props.customerEndpoints!}
                                            include="BOTH"
                                            value={this.props.consignor || null}
                                            onChange={e => this.props.onChangeConsignor!(e)}
                                            showCreate={false}
                                            placeholder={this.props.customerPlaceholder ? this.props.customerPlaceholder : "Include all"}
                                            isDisabledLink={this.props.selectedSaleId === null}
                                            involvementSaleFilters={this.props.involvementSaleFilters || []}
                                            involvementRoleFilters={this.props.involvementRoleFilters || []}
                                            isInvalid={this.props.wasValidated && this.props.consignor === null}
                                        />
                                    </div>
                                )}

                                {this.props.onChangeSelectHorsesForSale && (
                                    <div className="col-md-12 col-lg-4">
                                        <label htmlFor="consignor">
                                            Horse For Sale Name: <span className="text-danger">*</span>
                                        </label>
                                        <HorseForSaleSelect
                                            isDisabledLink={this.props.selectedSaleId === null}
                                            horseForSaleEndpoints={this.props.horseForSaleEndpoints!}
                                            value={this.props.horseForSale}
                                            placeholder="Include all"
                                            involvementSaleFilters={this.props.involvementSaleFilters || []}
                                            onChange={horseForSale => {
                                                this.props.onChangeSelectHorsesForSale!(horseForSale);
                                            }}
                                        />
                                    </div>
                                )}

                                {this.props.onChangeSelectIndex && (
                                    <SelectGroup
                                        label="Select an Index:"
                                        name="indexId"
                                        isRequired={true}
                                        className={`col-md-12 col-lg-4`}
                                        disabled={this.props.selectedSaleId === null}
                                        isInvalid={this.props.wasValidated && (this.props.indexId === null || this.props.indexId === "")}
                                        value={this.props.indexId || ""}
                                        onChange={e => {
                                            if (this.props.onChangeSelectIndex) {
                                                this.props.onChangeSelectIndex(e.target.value);
                                            }
                                        }}
                                    >
                                        <option value=""> Select an index...</option>
                                        {Object.keys(Indexes).map(index => (
                                            <option key={index} value={index}>
                                                {IndexesFriendlyName(index as Indexes)}
                                            </option>
                                        ))}
                                    </SelectGroup>
                                )}

                                {this.props.onChangeSelectInsuranceCompany && (
                                    <SelectGroup
                                        label="Select an Insurance Company"
                                        name="insuranceId"
                                        isRequired={true}
                                        className={`col-md-12 col-lg-4`}
                                        disabled={this.props.selectedSaleId === null}
                                        isInvalid={false}
                                        value={this.props.insuranceId || "0"}
                                        onChange={e => {
                                            if (this.props.onChangeSelectInsuranceCompany) {
                                                this.props.onChangeSelectInsuranceCompany(e.target.value);
                                            }
                                        }}
                                    >
                                        <option value="0">Include All</option>
                                        {configurationStore.insuranceCompanies.map(insuranceCompany => (
                                            <option key={insuranceCompany.id} value={insuranceCompany.id}>
                                                {insuranceCompany.businessName}
                                            </option>
                                        ))}
                                    </SelectGroup>
                                )}
                                <div className="col-md-12 col-lg-4 pt-4">
                                    <button type="submit" className="btn btn-primary mt-2 btn-block">
                                        <FontAwesomeIcon icon={faChartBar} className="mr-2 color-info" />
                                        Generate Report
                                    </button>
                                </div>
                            </div>
                        </form>
                        {this.props.content && (
                            <div className="row my-3">
                                <div className="col-12">
                                    <button type="text" className="btn btn-secondary mr-3" onClick={() => CopyReportIframe(this.reportFrame)}>
                                        <FontAwesomeIcon icon={faCopy} className="mr-2 color-info" />
                                        Copy Content
                                    </button>
                                    <button type="text" className="btn btn-secondary" onClick={() => PrintReportIframe(this.reportFrame)}>
                                        <FontAwesomeIcon icon={faPrint} className="mr-2 color-info" />
                                        Print Report
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="row mt-3 mx-0">
                            <div className="col-12 px-0">
                                <iframe
                                    className={"iframe-reports border-0"}
                                    frameBorder="0"
                                    style={this.props.content ? { height: "80vh", opacity: 1 } : {}}
                                    ref={f => (this.reportFrame = f)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/SimpleSaleReportComponent.tsx